// src/components/OrderProcess/CheckoutForm.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useOrder } from '../../contexts/OrderContext';
import { useAuth } from '../../contexts/AuthContext';
import styles from '../../styles/CheckoutForm.module.css';
import { collection, query, where, getDocs, doc, updateDoc, limit, increment } from 'firebase/firestore';
import { db } from '../../firebase';
import { getAnalytics, logEvent } from 'firebase/analytics';
import { trackInitiateCheckout, trackPurchase } from '../../utils/pixelEvents';
import { applyStoredDiscount } from '../DiscountBridge'; // Import applyStoredDiscount

// Your API base URL - replace with your actual backend URL
const API_URL = process.env.REACT_APP_API_URL || '/.netlify/functions';

const CheckoutForm = () => {
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();
  const {
    currentOrder,
    getPackagePrice,
    createOrder,
    getUserOrders,
    guestMode,
    syncOrderToFirebase,
  } = useOrder();
  const { currentUser, signup, login } = useAuth();
  const analytics = getAnalytics();

  const [processing, setProcessing] = useState(false);
  const [formComplete, setFormComplete] = useState(false);
  const [error, setError] = useState(null);
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [successData, setSuccessData] = useState(null);
  const [clientSecret, setClientSecret] = useState('');

  // Customer info state
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [country, setCountry] = useState('US');

  // Auth state for guest checkout
  const [showAuthForm, setShowAuthForm] = useState(guestMode);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSigningUp, setIsSigningUp] = useState(true);
  const [authError, setAuthError] = useState('');

  // Discount code state
  const [discountCode, setDiscountCode] = useState('');
  const [discountAmount, setDiscountAmount] = useState(0);
  const [discountError, setDiscountError] = useState('');
  const [isApplyingDiscount, setIsApplyingDiscount] = useState(false);
  const [activeDiscountId, setActiveDiscountId] = useState(null);

  // Calculate pricing
  const subtotal = getPackagePrice();
  const hasRushDelivery = currentOrder.details?.rushDelivery || false;
  const rushDeliveryFee = hasRushDelivery ? 30 : 0;

  // Calculate discount amount if applicable
  const discountValue = discountAmount > 0 ? ((subtotal + rushDeliveryFee) * discountAmount) / 100 : 0;

  // Calculate final total
  const total = subtotal + rushDeliveryFee - discountValue;

  // Track checkout initiation when component loads
  useEffect(() => {
    trackInitiateCheckout(total);
  }, [total]);

  // Check user authentication status on component mount
  useEffect(() => {
    if (currentUser) {
      setShowAuthForm(false);
      syncOrderToFirebase();
    } else {
      setShowAuthForm(true);
    }
  }, [currentUser, syncOrderToFirebase]);

  // Apply stored discount on mount
  useEffect(() => {
    const storedDiscount = localStorage.getItem('activeDiscount');
    if (storedDiscount && !discountAmount) {
      setDiscountCode(storedDiscount);
      applyDiscount(storedDiscount);
    }
  }, []);

  // Create PaymentIntent when the total changes (only for paid orders)
  useEffect(() => {
    if (total > 0 && !showAuthForm) {
      fetch(`/.netlify/functions/create-payment-intent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: total,
          metadata: {
            package: currentOrder.package,
            customer_email: currentUser?.email || email || '',
            discount_applied: discountAmount > 0 ? 'true' : 'false',
            discount_code: discountAmount > 0 ? discountCode : '',
            discount_amount: discountAmount > 0 ? discountAmount.toString() : '0',
          },
        }),
      })
        .then((res) => {
          if (!res.ok) {
            return res.text().then((text) => {
              console.error('Error response:', text);
              throw new Error('Network response was not ok');
            });
          }
          return res.json();
        })
        .then((data) => {
          if (data.clientSecret) {
            setClientSecret(data.clientSecret);
            setError(null);
          } else {
            console.error('No client secret in response:', data);
            setError('Invalid response from payment server');
          }
        })
        .catch((err) => {
          console.error('Error creating payment intent:', err);
          setError('Could not initialize payment. Please try again later.');
        });
    }
  }, [total, currentOrder.package, currentUser, discountAmount, discountCode, showAuthForm, email]);

  // Handle authentication form submission
  const handleAuth = async (e) => {
    e.preventDefault();
    setAuthError('');

    if (isSigningUp) {
      if (!name.trim()) {
        setAuthError('Name is required');
        return;
      }

      if (password.length < 6) {
        setAuthError('Password must be at least 6 characters');
        return;
      }

      if (password !== confirmPassword) {
        setAuthError('Passwords do not match');
        return;
      }
    }

    if (!email) {
      setAuthError('Email is required');
      return;
    }

    setProcessing(true);

    try {
      if (isSigningUp) {
        await signup(email, password, name);
      } else {
        await login(email, password);
      }

      await syncOrderToFirebase();
      setShowAuthForm(false);
      setAuthError('');
    } catch (error) {
      console.error('Authentication error:', error);
      if (error.code === 'auth/email-already-in-use') {
        setAuthError('An account with this email already exists. Try signing in instead.');
      } else if (error.code === 'auth/invalid-email') {
        setAuthError('Please enter a valid email address.');
      } else if (error.code === 'auth/weak-password') {
        setAuthError('Password is too weak. Use at least 6 characters.');
      } else if (error.code === 'auth/wrong-password' || error.code === 'auth/user-not-found') {
        setAuthError('Invalid email or password.');
      } else {
        setAuthError(error.message || 'An error occurred during authentication. Please try again.');
      }
    } finally {
      setProcessing(false);
    }
  };

  // Toggle between sign up and sign in modes
  const toggleAuthMode = () => {
    setIsSigningUp(!isSigningUp);
    setAuthError('');
  };

  // Handle card input changes
  const handleCardChange = (event) => {
    setFormComplete(event.complete);
    if (event.error) {
      setError(event.error.message);
    } else {
      setError(null);
    }
  };

  // Apply discount using applyStoredDiscount from DiscountBridge
  const applyDiscount = async (code = '') => {
    if (!discountCode && !code && !localStorage.getItem('activeDiscount')) return;

    const discountToApply = code || discountCode || localStorage.getItem('activeDiscount');
    if (!discountToApply) return;

    setIsApplyingDiscount(true);
    setDiscountError('');

    try {
      const result = await applyStoredDiscount(discountToApply);

      if (!result.applied) {
        if (result.reason === 'expired') {
          setDiscountError('This discount code has expired');
        } else if (result.reason === 'limit_reached') {
          setDiscountError('This discount code has reached its usage limit');
        } else {
          setDiscountError('Invalid discount code');
        }
        setDiscountAmount(0);
        setActiveDiscountId(null);
        setIsApplyingDiscount(false);
        return;
      }

      setDiscountAmount(result.amount);
      setActiveDiscountId(result.id);

      if (window.showDiscountToast) {
        window.showDiscountToast(
          discountToApply === 'MOM2025'
            ? "40% Mother's Day discount applied!"
            : `${result.amount}% discount applied!`
        );
      }
    } catch (error) {
      console.error('Error applying discount:', error);
      setDiscountError('Error applying discount code');
      setDiscountAmount(0);
      setActiveDiscountId(null);
    } finally {
      setIsApplyingDiscount(false);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Handle free orders (100% discount)
    if (total <= 0) {
      setProcessing(true);
      setError(null);

      try {
        const customerInfo = {
          fullName,
          email: currentUser?.email || email,
          phone,
          address,
          city,
          state,
          postalCode,
          country,
        };

        const orderData = {
          paymentId: 'free-order-' + Date.now(),
          customerInfo,
          discountApplied: discountAmount > 0,
          discountCode: discountAmount > 0 ? discountCode : null,
          discountAmount: discountAmount > 0 ? discountAmount : null,
          discountValue: discountValue > 0 ? discountValue : null,
          originalTotal: subtotal + rushDeliveryFee,
          finalTotal: 0,
        };

        try {
          logEvent(analytics, 'purchase', {
            transaction_id: orderData.paymentId,
            value: 0,
            currency: 'USD',
            affiliation: 'MakeMeSongs',
            items: [
              {
                item_id: currentOrder.package,
                item_name: `${currentOrder.package} Package`,
                item_category: currentOrder.genres?.join(', ') || 'No genre selected',
                price: subtotal,
                quantity: 1,
              },
            ],
            shipping: 0,
            tax: 0,
            coupon: discountCode || '',
            discount: discountValue.toFixed(2),
          });
        } catch (analyticsError) {
          console.error('Error logging analytics for free order:', analyticsError);
        }

        const orderResult = await createOrder(orderData.paymentId, customerInfo, orderData);

        if (activeDiscountId) {
          try {
            const discountRef = doc(db, 'discounts', activeDiscountId);
            await updateDoc(discountRef, {
              usageCount: increment(1),
            });
          } catch (discountError) {
            console.error('Error updating discount usage:', discountError);
          }
        }

        // Clear stored discount
        localStorage.removeItem('activeDiscount');

        trackPurchase(orderResult?.id || orderResult, total);

        setOrderSuccess(true);
        setSuccessData({
          orderId: orderResult?.id || orderResult,
          orderNumber: orderResult?.orderNumber || 'Unknown',
        });

        navigate('/confirmation', {
          state: {
            orderId: orderResult?.id || orderResult,
            orderNumber: orderResult?.orderNumber || 'Unknown',
            freeOrder: true,
          },
        });
      } catch (error) {
        console.error('Free order creation error:', error);
        setError(error.message || 'Failed to create your order. Please try again.');
      } finally {
        setProcessing(false);
      }

      return;
    }

    // Existing payment processing for paid orders
    if (!stripe || !elements || !formComplete || !clientSecret) {
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      console.log('Starting payment processing with Stripe');

      const billingDetails = {
        name: fullName,
        email: currentUser?.email || email,
        phone: phone,
        address: {
          line1: address,
          city: city,
          state: state,
          postal_code: postalCode,
          country: country,
        },
      };

      const result = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement),
          billing_details: billingDetails,
        },
      });

      if (result.error) {
        console.error('Payment confirmation error:', result.error);
        setError(result.error.message);
        setProcessing(false);
        return;
      }

      if (result.paymentIntent.status === 'succeeded') {
        console.log('Payment processed successfully with ID:', result.paymentIntent.id);

        try {
          logEvent(analytics, 'purchase', {
            transaction_id: result.paymentIntent.id,
            value: total,
            currency: 'USD',
            affiliation: 'MakeMeSongs',
            items: [
              {
                item_id: currentOrder.package,
                item_name: `${currentOrder.package} Package`,
                item_category: currentOrder.genres?.join(', ') || 'No genre selected',
                price: subtotal,
                quantity: 1,
              },
            ],
            shipping: 0,
            tax: 0,
            coupon: discountCode || '',
            discount: discountValue.toFixed(2),
          });
        } catch (analyticsError) {
          console.error('Error logging purchase to analytics:', analyticsError);
        }

        try {
          const customerInfo = {
            fullName,
            email: currentUser?.email || email,
            phone,
            address,
            city,
            state,
            postalCode,
            country,
          };

          const orderData = {
            paymentId: result.paymentIntent.id,
            customerInfo,
            discountApplied: discountAmount > 0,
            discountCode: discountAmount > 0 ? discountCode : null,
            discountAmount: discountAmount > 0 ? discountAmount : null,
            discountValue: discountValue > 0 ? discountValue : null,
            originalTotal: subtotal + rushDeliveryFee,
            finalTotal: total,
          };

          console.log('Creating order with payment ID:', result.paymentIntent.id);
          const orderResult = await createOrder(result.paymentIntent.id, customerInfo, orderData);
          console.log('Order created successfully:', orderResult);

          if (activeDiscountId) {
            try {
              const discountRef = doc(db, 'discounts', activeDiscountId);
              await updateDoc(discountRef, {
                usageCount: increment(1),
              });
              console.log('Discount usage updated');
            } catch (discountError) {
              console.error('Error updating discount usage:', discountError);
            }
          }

          // Clear stored discount
          localStorage.removeItem('activeDiscount');

          trackPurchase(orderResult?.id || orderResult, total);

          const orderId = orderResult?.id || orderResult;
          const orderNumber = orderResult?.orderNumber || 'Unknown';

          setOrderSuccess(true);
          setSuccessData({ orderId, orderNumber });

          navigate('/confirmation', {
            state: {
              orderId,
              orderNumber,
              paymentIntentId: result.paymentIntent.id,
            },
          });
        } catch (orderError) {
          console.error('Order creation error:', orderError);

          try {
            console.log('Checking if order was created despite error...');
            const recentOrders = await getUserOrders();

            if (recentOrders && recentOrders.length > 0) {
              const recentOrder = recentOrders[0];
              const orderCreatedTime = new Date(recentOrder.createdAt);
              const now = new Date();
              const secondsAgo = (now - orderCreatedTime) / 1000;

              if (secondsAgo < 60) {
                console.log('Found recent order that was likely just created:', recentOrder);

                trackPurchase(recentOrder.id, total);

                setOrderSuccess(true);
                setSuccessData({
                  orderId: recentOrder.id,
                  orderNumber: recentOrder.orderNumber,
                  recovered: true,
                });

                navigate('/confirmation', {
                  state: {
                    orderId: recentOrder.id,
                    orderNumber: recentOrder.orderNumber,
                    recovered: true,
                    paymentIntentId: result.paymentIntent.id,
                  },
                });
                return;
              }
            }
          } catch (recoveryError) {
            console.error('Error trying to recover from order creation failure:', recoveryError);
          }

          setError(
            orderError.message ||
              'Payment successful, but failed to create your order. Please contact support with your payment ID: ' +
                result.paymentIntent.id
          );
        }
      } else {
        setError(`Payment status: ${result.paymentIntent.status}. Please try again.`);
      }
    } catch (error) {
      console.error('Payment error:', error);
      setError('Payment processing failed. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  // Check for recent orders if there's a database error
  useEffect(() => {
    if (error && error.includes('database')) {
      const timer = setTimeout(async () => {
        try {
          console.log('Checking for recently created orders...');
          const recentOrders = await getUserOrders();

          if (recentOrders && recentOrders.length > 0) {
            const recentOrder = recentOrders[0];
            const orderCreatedTime = new Date(recentOrder.createdAt);
            const now = new Date();
            const secondsAgo = (now - orderCreatedTime) / 1000;

            if (secondsAgo < 120) {
              console.log('Found recent order:', recentOrder);

              trackPurchase(recentOrder.id, total);

              setOrderSuccess(true);
              setSuccessData({
                orderId: recentOrder.id,
                orderNumber: recentOrder.orderNumber,
                recovered: true,
              });

              navigate('/confirmation', {
                state: {
                  orderId: recentOrder.id,
                  orderNumber: recentOrder.orderNumber,
                  recovered: true,
                },
              });
            }
          }
        } catch (err) {
          console.error('Error checking for recent orders:', err);
        }
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [error, getUserOrders, navigate, total]);

  const handleBack = () => {
    navigate('/details');
  };

  if (orderSuccess) {
    return (
      <div className={styles.checkoutContainer}>
        <h1 className={styles.title}>Order Successful!</h1>
        <div className={styles.content} style={{ textAlign: 'center', padding: '2rem' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>✅</div>
          <h2>Your order was processed successfully</h2>
          <p>Order number: {successData?.orderNumber || 'Processing...'}</p>
          <p>Redirecting to confirmation page...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.checkoutContainer}>
      <h1 className={styles.title}>Complete Your Order</h1>

      <div className={styles.content}>
        <div className={styles.orderSummary}>
          <h2 className={styles.sectionTitle}>Order Summary</h2>

          <div className={styles.summarySection}>
            <h3 className={styles.packageTitle}>{currentOrder.package} Package</h3>
            <ul className={styles.featuresList}>
              {currentOrder.package === 'Basic' && (
                <>
                  <li>One custom song (up to 2 minutes)</li>
                  <li>One music style</li>
                  <li>Basic personalization</li>
                  <li>No Revisions</li>
                  <li>No Variations</li>
                </>
              )}
              {currentOrder.package === 'Premium' && (
                <>
                  <li>One custom song (up to 3 minutes)</li>
                  <li>Two blended music styles</li>
                  <li>Advanced personalization</li>
                  <li>One Revision</li>
                  <li>Two Variations</li>
                </>
              )}
              {currentOrder.package === 'Deluxe' && (
                <>
                  <li>One custom song (up to 4 minutes)</li>
                  <li>Three blended music styles</li>
                  <li>Premium personalization</li>
                  <li>Two Revisions</li>
                  <li>Four Variations</li>
                </>
              )}
            </ul>
          </div>

          <div className={styles.summarySection}>
            <h3 className={styles.subSectionTitle}>
              Selected Genre{currentOrder.genres.length > 1 ? 's' : ''}
            </h3>
            <div className={styles.genreList}>
              {currentOrder.genres.map((genre) => (
                <span key={genre} className={styles.genreBadge}>
                  {genre}
                </span>
              ))}
            </div>
          </div>

          <div className={styles.discountSection}>
            <h3 className={styles.subSectionTitle}>Discount Code</h3>
            <div className={styles.discountInputContainer}>
              <input
                type="text"
                className={styles.discountInput}
                value={discountCode}
                onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                placeholder="Enter code"
                disabled={isApplyingDiscount || discountAmount > 0}
              />
              <button
                type="button"
                className={styles.discountButton}
                onClick={() => applyDiscount()}
                disabled={isApplyingDiscount || !discountCode || discountAmount > 0}
              >
                {isApplyingDiscount ? 'Applying...' : 'Apply'}
              </button>
            </div>
            {discountError && <div className={styles.discountError}>{discountError}</div>}
            {discountAmount > 0 && (
              <div className={styles.discountApplied}>
                {discountAmount}% discount applied
              </div>
            )}
          </div>

          <div className={styles.priceSection}>
            <div className={styles.priceItem}>
              <span>{currentOrder.package} Package</span>
              <span>${subtotal.toFixed(2)}</span>
            </div>
            {hasRushDelivery && (
              <div className={styles.priceItem}>
                <span>Rush Delivery Fee</span>
                <span>${rushDeliveryFee.toFixed(2)}</span>
              </div>
            )}
            {discountAmount > 0 && (
              <div className={styles.priceItem}>
                <span>Discount ({discountAmount}%)</span>
                <span>-${discountValue.toFixed(2)}</span>
              </div>
            )}
            <div className={styles.priceDivider}></div>
            <div className={styles.priceTotal}>
              <span>Total</span>
              <span>${total.toFixed(2)}</span>
            </div>
          </div>
        </div>

        {showAuthForm ? (
          <div className={styles.authSection}>
            <h2 className={styles.sectionTitle}>
              {isSigningUp ? 'Create an Account' : 'Sign In'} to Complete Your Order
            </h2>
            <p className={styles.authDescription}>
              {isSigningUp
                ? 'Create an account to access your custom song, make revisions, download and track your order.'
                : 'Sign in to your existing account to complete your purchase.'}
            </p>

            <form onSubmit={handleAuth} className={styles.authForm}>
              {authError && <div className={styles.error}>{authError}</div>}

              {isSigningUp && (
                <div>
                  <label className={styles.formLabel}>Name</label>
                  <input
                    type="text"
                    className={styles.formInput}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                  />
                </div>
              )}

              <div>
                <label className={styles.formLabel}>Email</label>
                <input
                  type="email"
                  className={styles.formInput}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>

              <div>
                <label className={styles.formLabel}>Password</label>
                <input
                  type="password"
                  className={styles.formInput}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>

              {isSigningUp && (
                <div>
                  <label className={styles.formLabel}>Confirm Password</label>
                  <input
                    type="password"
                    className={styles.formInput}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                  />
                </div>
              )}

              <div className={styles.formActions}>
                <button
                  type="button"
                  onClick={handleBack}
                  className={styles.backButton}
                  disabled={processing}
                >
                  Back to Details
                </button>
                <button
                  type="submit"
                  className={styles.continueButton}
                  disabled={processing}
                >
                  {processing
                    ? 'Processing...'
                    : isSigningUp
                    ? 'Create Account & Continue'
                    : 'Sign In & Continue'}
                </button>
              </div>

              <div className={styles.authToggle}>
                {isSigningUp ? 'Already have an account?' : "Don't have an account?"}
                <button type="button" onClick={toggleAuthMode} className={styles.toggleButton}>
                  {isSigningUp ? 'Sign In' : 'Create Account'}
                </button>
              </div>
            </form>
          </div>
        ) : (
          <div className={styles.paymentSection}>
            <h2 className={styles.sectionTitle}>Customer Information</h2>

            <div>
              <label className={styles.formLabel}>Full Name</label>
              <input
                type="text"
                className={styles.formInput}
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
              />
            </div>

            <div>
              <label className={styles.formLabel}>Phone Number</label>
              <input
                type="tel"
                className={styles.formInput}
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required
              />
            </div>

            <div>
              <label className={styles.formLabel}>Country</label>
              <select
                className={styles.formSelect}
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                required
              >
                <option value="US">United States</option>
                <option value="CA">Canada</option>
                <option value="GB">United Kingdom</option>
                <option value="AU">Australia</option>
                <option value="NZ">New Zealand</option>
                <option value="IE">Ireland</option>
                <option value="JM">Jamaica</option>
                <option value="BZ">Belize</option>
                <option value="BS">Bahamas</option>
                <option value="BB">Barbados</option>
                <option value="AG">Antigua and Barbuda</option>
                <option value="TT">Trinidad and Tobago</option>
                <option value="GY">Guyana</option>
                <option value="GD">Grenada</option>
                <option value="DM">Dominica</option>
                <option value="KN">Saint Kitts and Nevis</option>
                <option value="LC">Saint Lucia</option>
                <option value="VC">Saint Vincent and the Grenadines</option>
                <option value="SG">Singapore</option>
                <option value="ZA">South Africa</option>
                <option value="NA">Namibia</option>
                <option value="BW">Botswana</option>
                <option value="GH">Ghana</option>
                <option value="NG">Nigeria</option>
                <option value="LR">Liberia</option>
                <option value="SL">Sierra Leone</option>
                <option value="GM">Gambia</option>
                <option value="FJ">Fiji</option>
                <option value="PH">Philippines</option>
                <option value="PG">Papua New Guinea</option>
                <option value="SS">South Sudan</option>
                <option value="MT">Malta</option>
              </select>
            </div>

            <div>
              <label className={styles.formLabel}>Address</label>
              <input
                type="text"
                className={styles.formInput}
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                required
              />
            </div>

            <div>
              <label className={styles.formLabel}>City</label>
              <input
                type="text"
                className={styles.formInput}
                value={city}
                onChange={(e) => setCity(e.target.value)}
                required
              />
            </div>

            <div>
              <label className={styles.formLabel}>State/Province</label>
              <input
                type="text"
                className={styles.formInput}
                value={state}
                onChange={(e) => setState(e.target.value)}
                required
              />
            </div>

            <div>
              <label className={styles.formLabel}>Postal/ZIP Code</label>
              <input
                type="text"
                className={styles.formInput}
                value={postalCode}
                onChange={(e) => setPostalCode(e.target.value)}
                required
              />
            </div>

            {total > 0 ? (
              <>
                <h2 className={styles.sectionTitle}>Payment Information</h2>
                <form onSubmit={handleSubmit}>
                  <div className={styles.cardElement}>
                    <label className={styles.cardLabel}>Credit or Debit Card</label>
                    <div className={styles.cardContainer}>
                      <CardElement
                        options={{
                          style: {
                            base: {
                              fontSize: '16px',
                              fontFamily: '"Segoe UI", sans-serif',
                              '::placeholder': { color: '#aab7c4' },
                            },
                            invalid: { color: '#fa755a' },
                          },
                        }}
                        onChange={handleCardChange}
                      />
                    </div>
                  </div>

                  {error && (
                    <div
                      className={styles.error}
                      style={{
                        color: '#e53e3e',
                        padding: '0.75rem',
                        margin: '0.75rem 0',
                        backgroundColor: '#fff5f5',
                        borderRadius: '0.25rem',
                        border: '1px solid #fc8181',
                      }}
                    >
                      {error}
                      {error.includes('database') && (
                        <div style={{ marginTop: '0.5rem', fontSize: '0.9em' }}>
                          Note: If you see this error but are redirected to the confirmation page,
                          your order was actually created successfully.
                        </div>
                      )}
                    </div>
                  )}

                  <div className={styles.securityNote}>
                    Your payment information is securely processed by Stripe. We never store your card
                    details.
                  </div>

                  <div className={styles.formActions}>
                    <button
                      type="button"
                      onClick={handleBack}
                      className={styles.backButton}
                      disabled={processing}
                    >
                      Back to Details
                    </button>
                    <button
                      type="submit"
                      className={styles.payButton}
                      disabled={!stripe || !formComplete || processing || !clientSecret}
                    >
                      {processing ? 'Processing...' : `Pay $${total.toFixed(2)}`}
                    </button>
                  </div>
                  <div className={styles.termsAgreement}>
                    By proceeding with payment, you agree to our{' '}
                    <a href="/terms-privacy#terms" target="_blank" rel="noopener noreferrer">
                      Terms of Service
                    </a>
                    ,{' '}
                    <a href="/terms-privacy#privacy" target="_blank" rel="noopener noreferrer">
                      Privacy Policy
                    </a>
                    , and{' '}
                    <a href="/terms-privacy#refund" target="_blank" rel="noopener noreferrer">
                      Refund Policy
                    </a>
                    .
                  </div>
                </form>
              </>
            ) : (
              <form onSubmit={handleSubmit}>
                {error && (
                  <div
                    className={styles.error}
                    style={{
                      color: '#e53e3e',
                      padding: '0.75rem',
                      margin: '0.75rem 0',
                      backgroundColor: '#fff5f5',
                      borderRadius: '0.25rem',
                      border: '1px solid #fc8181',
                    }}
                  >
                    {error}
                  </div>
                )}

                <div className={styles.freeOrderMessage}>
                  Your order is free! No payment information required.
                </div>

                <div className={styles.formActions}>
                  <button
                    type="button"
                    onClick={handleBack}
                    className={styles.backButton}
                    disabled={processing}
                  >
                    Back to Details
                  </button>
                  <button
                    type="submit"
                    className={styles.payButton}
                    disabled={processing}
                  >
                    {processing ? 'Processing...' : 'Complete Free Order'}
                  </button>
                </div>
                <div className={styles.termsAgreement}>
                  By completing this order, you agree to our{' '}
                  <a href="/terms-privacy#terms" target="_blank" rel="noopener noreferrer">
                    Terms of Service
                  </a>
                  ,{' '}
                  <a href="/terms-privacy#privacy" target="_blank" rel="noopener noreferrer">
                    Privacy Policy
                  </a>
                  , and{' '}
                  <a href="/terms-privacy#refund" target="_blank" rel="noopener noreferrer">
                    Refund Policy
                  </a>
                  .
                </div>
              </form>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CheckoutForm;